import { ChangeEvent, useMemo, useState } from "react"
import { DateRange } from "react-day-picker"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { addDays, startOfDay } from "date-fns"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DateRangeInput } from "@/components/ui/date-range-input"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { Input, TextArea } from "@/components/ui/input"
import { PrefixSuffixInput } from "@/components/ui/prefix-suffix-input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { formatDate } from "@/lib/format-date"
import { joinStrings } from "@/lib/joinString"
import { textAreaDefaultMaxLength } from "@/models/common"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import {
  CERTIFICATE_TYPE,
  ChSickLeaveCertificateSchema,
  ChSickLeaveCertificateType,
  SICK_LEAVE_REASON_TYPE,
  WORK_CAPACITY_TYPE,
} from "@/modules/patients/detail/sick-leave-certificate/ch/schema/ch-sick-leave-certificate-schema"
import {
  chSickLeaveFragment$data,
  chSickLeaveFragment$key,
} from "@/modules/patients/detail/sick-leave-certificate/ch/services/__generated__/chSickLeaveFragment.graphql"
import { SickLeaveReason } from "@/modules/patients/detail/sick-leave-certificate/ch/services/__generated__/useChCreateSickLeaveMutation.graphql"
import { chSickLeaveFragment } from "@/modules/patients/detail/sick-leave-certificate/ch/services/chSickLeaveFragment"
import { useChCreateSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChCreateSickLeaveMutation"
import { useChDeleteSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChDeleteSickLeaveMutation"
import {
  SickLeavesForFollowUpResult,
  useChFollowUpSickLeaveQuery,
} from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChFollowUpSickLeaveQuery"
import { useChGenerateSickLeaveDocumentMutation } from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChGenerateSickLeaveDocumentMutation"
import { useChUpdateSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/ch/services/useChUpdateSickLeaveMutation"
import { deSickLeaveFragment$key } from "@/modules/patients/detail/sick-leave-certificate/de/services/__generated__/deSickLeaveFragment.graphql"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  DOCUMENT_LANGUAGES,
  getDefaultDocumentLanguageValue,
  getDocumentLanguageLabel,
} from "@/modules/patients/libs/document-languages"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface ChSickLeaveCertificateDrawerProps extends FetchKeyProps {
  progressNoteId: string
  sickLeaveRef?: chSickLeaveFragment$key | deSickLeaveFragment$key
}

// This function is to get a latest employer name to pre-fill to employer field when create CH sick leave
const getLatestChEmployerName = (progressNoteAndParentData: ProgressNoteAndParentData[]) => {
  const chSickLeaveDocuments = progressNoteAndParentData
    .flatMap((progressNote) =>
      progressNote.episodeOfCareProgressNoteData.sickLeave &&
      "employerName" in progressNote.episodeOfCareProgressNoteData.sickLeave
        ? (progressNote.episodeOfCareProgressNoteData.sickLeave ?? [])
        : [],
    )
    .flatMap((item) => item)

  if (!chSickLeaveDocuments.length) return null

  const latestSickLeaveDocument = chSickLeaveDocuments.reduce((latest, document) => {
    const latestDate = latest.updatedAt ? new Date(latest.updatedAt) : new Date(latest.createdAt)
    const currentDate = document.updatedAt ? new Date(document.updatedAt) : new Date(document.createdAt)

    return currentDate > latestDate ? document : latest
  }, chSickLeaveDocuments[0])

  return latestSickLeaveDocument.employerName
}

const ChSickLeaveCertificateDrawer = withErrorBoundary((props: ChSickLeaveCertificateDrawerProps) => {
  const sickLeaveData = useFragment(chSickLeaveFragment, props.sickLeaveRef as chSickLeaveFragment$key)
  const progressNoteAndParentData = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)

  // Memoize progressNoteIds to prevent useChFollowUpSickLeaveQuery re-fetching on every render
  const progressNoteIds = useMemo(
    () => progressNoteAndParentData.flatMap((item) => item.episodeOfCareProgressNoteData.id),
    [progressNoteAndParentData],
  )

  const followUpCertificateList = useChFollowUpSickLeaveQuery(progressNoteIds, sickLeaveData?.followUpId)

  if (!followUpCertificateList) return null

  return (
    <ChSickLeaveCertificateDrawerData
      followUpCertificateList={followUpCertificateList}
      progressNoteAndParentData={progressNoteAndParentData}
      progressNoteId={props.progressNoteId}
      progressNoteIds={progressNoteIds}
      sickLeaveData={sickLeaveData}
    />
  )
}, "ChSickLeaveCertificateDrawer")

type ChSickLeaveCertificateDrawerDataProps = {
  followUpCertificateList: NonNullable<SickLeavesForFollowUpResult>
  progressNoteAndParentData: ProgressNoteAndParentData[]
  progressNoteId: string
  progressNoteIds: string[]
  sickLeaveData: chSickLeaveFragment$data
}

const ChSickLeaveCertificateDrawerData = (props: ChSickLeaveCertificateDrawerDataProps) => {
  const { followUpCertificateList, progressNoteAndParentData, progressNoteId, progressNoteIds, sickLeaveData } = props
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const today = startOfDay(new Date())
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const patientId = usePatientParam()
  const [createSickLeave, isCreating] = useChCreateSickLeaveMutation()
  const [updateSickLeave, isUpdating] = useChUpdateSickLeaveMutation()
  const [deleteSickLeave] = useChDeleteSickLeaveMutation()
  const [generateSickLeaveDocument, isGenerating] = useChGenerateSickLeaveDocumentMutation()
  const existingTimeFrame = sickLeaveData
    ? { from: new Date(sickLeaveData.from), to: new Date(sickLeaveData.to) }
    : { from: today }
  const [durationValue, setDurationValue] = useState<DateRange | undefined>(existingTimeFrame)
  const contextAppointment = useContextAppointment()

  const formattedFollowUpCertificateList = followUpCertificateList.map((item) => {
    const from = formatDate(new Date(item.from))
    const to = formatDate(new Date(item.to))
    return {
      value: {
        id: item.id,
        to: item.to,
      },
      label: joinStrings(
        [`${t("patient.sickLeaveCertificate.followUpCertificateOn")} ${from}`, to, item.employerName],
        " - ",
      ),
    }
  })

  const certificateList = [
    {
      value: {
        id: "FIRST",
        to: "",
      },
      label: t("patient.sickLeaveCertificate.firstCertificate"),
    },
    ...formattedFollowUpCertificateList,
  ]

  const updatedAt = sickLeaveData?.updatedAt ?? sickLeaveData?.createdAt
  const updatedBy = sickLeaveData?.updatedBy ?? sickLeaveData?.createdBy
  const employerName = getLatestChEmployerName(progressNoteAndParentData) ?? ""
  const followUp = followUpCertificateList.find((item) => item.id === sickLeaveData?.followUpId)
  const defaultValuesFollowUp = {
    id: "",
    to: "",
  }
  const followUpInfo = followUp ? { id: followUp.id, to: followUp.to } : defaultValuesFollowUp

  const existingData = sickLeaveData
    ? ChSickLeaveCertificateSchema.parse({
        ...sickLeaveData,
        certificateType:
          sickLeaveData?.certificateType === CERTIFICATE_TYPE.FIRST ? sickLeaveData?.certificateType : followUp?.id,
        capacityType: sickLeaveData.workCapacityType,
        timeFrame: existingTimeFrame,
        followUpInfo,
      })
    : null

  const form = useForm<ChSickLeaveCertificateType>({
    mode: "all",
    resolver: zodResolver(ChSickLeaveCertificateSchema),
    defaultValues: existingData ?? {
      language: getDefaultDocumentLanguageValue(contextAppointment?.language),
      certificateType: CERTIFICATE_TYPE.FIRST,
      reason: SICK_LEAVE_REASON_TYPE.SICKNESS,
      capacityType: WORK_CAPACITY_TYPE.EMPLOYMENT_RATE,
      timeFrame: existingTimeFrame,
      workCapacity: 0,
      employerName: employerName,
      additionalInformation: "",
      followUpInfo: defaultValuesFollowUp,
    },
  })

  const {
    control,
    setValue,
    trigger,
    getValues,
    register,
    resetField,
    formState: { errors, isSubmitted, isDirty },
  } = form

  useDirtyDrawerMaker(isDirty)

  const isEditMode = sickLeaveData?.id
  const isAddMode = !isEditMode && patientId && progressNoteId

  const handleRangeChange = (range?: DateRange) => {
    if (range?.from) {
      setValue(
        "timeFrame",
        { from: range.from, to: range.to },
        {
          shouldValidate: isSubmitted,
          shouldDirty: true,
        },
      )
      setDurationValue({ from: range.from, to: range.to })
      if (range.to) {
        trigger("timeFrame")
      }
    }
  }

  const getCertificateTypeLabel = (value: string) =>
    certificateList.find((option) => option.value.id === value)?.label ?? ""

  const handleRangeInputChange = (_e: ChangeEvent<HTMLInputElement>, range?: DateRange) => {
    const selectedRange = { from: range?.from ?? today, to: range?.to }
    setValue("timeFrame", selectedRange, { shouldValidate: true, shouldDirty: true })
  }

  const handleSubmit = (data: ChSickLeaveCertificateType) => {
    const { timeFrame, certificateType, followUpInfo } = data
    if (!timeFrame.to || !timeFrame.from || !progressNoteId) return

    const isFollowUp = certificateType !== CERTIFICATE_TYPE.FIRST
    const formattedData = {
      ...data,
      from: new DateOnly(timeFrame.from).toString(),
      to: new DateOnly(timeFrame.to).toString(),
      certificateType: isFollowUp ? CERTIFICATE_TYPE.FOLLOW_UP : CERTIFICATE_TYPE.FIRST,
      followUpId: isFollowUp ? followUpInfo.id : undefined,
    }

    if (isAddMode) {
      createSickLeave({
        variables: {
          input: {
            additionalInformation: formattedData.additionalInformation,
            capacityType: formattedData.capacityType,
            certificateType: formattedData.certificateType,
            employerName: formattedData.employerName,
            followUpId: formattedData.followUpId,
            from: formattedData.from,
            language: formattedData.language,
            reason: formattedData.reason,
            to: formattedData.to,
            workCapacity: formattedData.workCapacity,
            patientId,
            progressNoteId,
          },
        },
        onCompleted(response) {
          const id = response.ch_createSickLeave?.ch_SickLeave?.id
          if (id) {
            handleGenerateSickLeave(id)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const sickLeave = data?.ch_createSickLeave?.ch_SickLeave

            if (sickLeave?.id) {
              const newRecordProxy = store.get(sickLeave.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_sickLeave")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }

    if (isEditMode) {
      updateSickLeave({
        variables: {
          input: {
            additionalInformation: formattedData.additionalInformation,
            capacityType: formattedData.capacityType,
            certificateType: formattedData.certificateType,
            employerName: formattedData.employerName,
            followUpId: formattedData.followUpId,
            from: formattedData.from,
            language: formattedData.language,
            reason: formattedData.reason,
            to: formattedData.to,
            workCapacity: formattedData.workCapacity,
            id: sickLeaveData.id,
          },
        },
        onCompleted(response) {
          const id = response.ch_updateSickLeave?.ch_SickLeave?.id
          if (id) {
            handleGenerateSickLeave(id)
          }
        },
      })
    }
  }

  const handleGenerateSickLeave = (id: string) => {
    if (id) {
      generateSickLeaveDocument({
        params: {
          id,
          progressNoteIds,
        },
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }
  }

  const onReasonTypeChange = (value: SickLeaveReason) => {
    setValue("reason", value, { shouldDirty: true })
  }

  const handleCloseDrawer = () => {
    closeDrawer()
    FaroInsightsWrapper.pushEvent("documents_sickLeave_close")
  }

  const handleOpenConfirmDialog = (isOpen: boolean) => {
    setIsOpenConfirmDialog(isOpen)
  }

  const handleDelete = () => {
    if (progressNoteId && sickLeaveData) {
      deleteSickLeave({
        variables: {
          input: {
            id: sickLeaveData.id,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          handleOpenConfirmDialog(false)
          toast({
            description: t("patient.sickLeaveCertificate.deleteMessage.success.ch"),
          })
        },
      })
    }
  }

  const isInvalidDateDisabled = (day: Date) => {
    const { to } = getValues("followUpInfo")
    const certificateType = getValues("certificateType")
    if (certificateType !== "FIRST" && to) {
      const formattedTo = new Date(to)

      // Disable all dates before the to date
      return formattedTo && day <= formattedTo
    } else return false
  }

  return (
    <Drawer open onOpenChange={handleCloseDrawer}>
      <FormProvider {...form}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 py-4.5 pr-4" data-testid="sick-leave-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("episodeOfCare.progressNote.sickLeaveCertificate.ch")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="sick-leave-body">
            <FormField
              control={control}
              name="language"
              render={({ field: { onChange, value, ref } }) => (
                <div className="mb-4">
                  <Select value={value} onValueChange={onChange} {...ref}>
                    <SelectTrigger className="w-56.5">
                      <SelectValue asChild>
                        <LabelAndSelectValue
                          data-testid="document-language"
                          label={t("patient.therapyPrescription.documentLanguage")}
                          value={t(getDocumentLanguageLabel(value))}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_LANGUAGES.map((language) => (
                        <SelectItem
                          value={language.value}
                          key={language.value}
                          data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                          {t(language.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />

            <DateRangeInput
              label={t("patient.sickLeaveCertificate.timeFrame")}
              onSelectDays={handleRangeChange}
              onInputChange={handleRangeInputChange}
              error={errors?.timeFrame?.message}
              translateError
              required
              value={durationValue}
              data-testid="date-range-input"
              disabledMatcher={isInvalidDateDisabled}
            />

            <FormField
              control={control}
              name="certificateType"
              render={({ field: { onChange, value, ref } }) => (
                <div className="mb-4">
                  <Select
                    value={value}
                    onValueChange={(value) => {
                      const followUpCertificate = followUpCertificateList.find((item) => item.id === value)
                      if (value === "FIRST" || (value !== "FIRST" && !followUpCertificate)) resetField("followUpInfo")
                      else if (followUpCertificate) {
                        if (sickLeaveData?.followUpId === value) {
                          setDurationValue(existingTimeFrame)
                          setValue("timeFrame", existingTimeFrame)
                        } else {
                          const timeFrame = { from: addDays(new Date(followUpCertificate.to), 1) }
                          setValue("timeFrame", timeFrame, { shouldValidate: true, shouldDirty: true })
                          setDurationValue(timeFrame)
                        }

                        setValue("followUpInfo", {
                          id: value,
                          to: followUpCertificate?.to,
                        })
                      }
                      onChange(value)
                      trigger("timeFrame")
                    }}
                    {...ref}>
                    <SelectTrigger>
                      <SelectValue asChild placeholder={t("patient.sickLeaveCertificate.typeOfCertificate")}>
                        <LabelAndSelectValue
                          required
                          data-testid="certificate-type"
                          label={t("patient.sickLeaveCertificate.typeOfCertificate")}
                          value={getCertificateTypeLabel(value)}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-w-(--radix-select-trigger-width)">
                      {certificateList.map(({ value, label }) => (
                        <SelectItem
                          className="block"
                          key={value.id}
                          value={value.id}
                          data-testid={`${value.id.toLocaleLowerCase()}-certificate`}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <div className="grid gap-2">
              <p className="text-on-surface-subdued text-sm">{t("patient.sickLeaveCertificate.reason")}</p>
              <RadioGroup className="flex" onValueChange={onReasonTypeChange} defaultValue={getValues("reason")}>
                <RadioGroupItem value={SICK_LEAVE_REASON_TYPE.SICKNESS} data-testid="sickness-reason-radio-item">
                  <RadioGroupLabel>{t("patient.sickLeaveCertificate.sickness")}</RadioGroupLabel>
                </RadioGroupItem>
                <RadioGroupItem value={SICK_LEAVE_REASON_TYPE.ACCIDENT} data-testid="accident-reason-radio-item">
                  <RadioGroupLabel>{t("patient.sickLeaveCertificate.accident")}</RadioGroupLabel>
                </RadioGroupItem>
              </RadioGroup>
            </div>
            <FormField
              control={control}
              name="workCapacity"
              render={({ field: { value, onChange } }) => (
                <PrefixSuffixInput
                  label={t("patient.sickLeaveCertificate.workCapacity")}
                  data-testid="work-capacity-input"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "")
                    onChange(value ? +value : null)
                    trigger("workCapacity")
                  }}
                  value={value ?? 0}
                  error={errors.workCapacity?.message}
                  maxLength={4}
                  suffix={
                    <FormField
                      control={control}
                      name="capacityType"
                      render={({ field: { value, onChange } }) => (
                        <div>
                          <Select
                            value={value}
                            onValueChange={(type) => {
                              onChange(type)
                              trigger("workCapacity")
                            }}>
                            <SelectTrigger className="text-on-surface-subdued h-full rounded-l-none border-none px-2.5">
                              <SelectValue asChild>
                                <LabelAndSelectValue
                                  label={
                                    getValues("capacityType") === WORK_CAPACITY_TYPE.HOURS_PER_WEEK
                                      ? t("patient.sickLeaveCertificate.h/week")
                                      : "%"
                                  }
                                  className="relative top-0 left-0 mr-4"
                                />
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                              <SelectItem value={WORK_CAPACITY_TYPE.EMPLOYMENT_RATE}>
                                <p>%</p>
                              </SelectItem>
                              <SelectItem value={WORK_CAPACITY_TYPE.HOURS_PER_WEEK}>
                                <p>{t("patient.sickLeaveCertificate.h/week")}</p>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    />
                  }
                />
              )}
            />
            <Input
              {...register("employerName")}
              label={t("patient.sickLeaveCertificate.employer")}
              error={errors.employerName?.message}
              translateError
              errorClasses="truncate"
              required
              data-testid="employer-name-input"
            />
            <TextArea
              {...register("additionalInformation")}
              label={t("patient.sickLeaveCertificate.additionalInformation")}
              wrapperProps={{ className: "mt-4" }}
              error={errors.additionalInformation?.message}
              data-testid="additional-information-input"
              textLimit={textAreaDefaultMaxLength}
            />
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="sick-leave-footer">
            {sickLeaveData && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_sickLeave_delete")
                    handleOpenConfirmDialog(true)
                  }}
                  data-testid="ch-sick-leave-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  handleCloseDrawer()
                }}
                data-testid="sick-leave-close-button">
                {t("common.button.close")}
              </Button>
              <Button
                className="min-w-16.25"
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_sickLeave_save")
                  form.handleSubmit(handleSubmit)()
                }}
                disabled={sickLeaveData && !isDirty}
                data-testid="sick-leave-save-button"
                isLoading={isCreating || isUpdating || isGenerating}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>

      <DeprecatedConfirmationDialog
        title={t("patient.sickLeaveCertificate.deleteConfirmTitle.ch")}
        content={t("episodeOfCare.deleteProgressNote.content")}
        open={isOpenConfirmDialog}
        onConfirm={handleDelete}
        confirmVariant="danger"
        onOpenChange={handleOpenConfirmDialog}
      />
    </Drawer>
  )
}

export { ChSickLeaveCertificateDrawer }
